import { useState, useRef } from "react";
import type { Editor, JSONContent } from "@tiptap/react";
import { parseTemplate } from "@/parser/templateParser";
import type { ParseError } from "@/parser/templateParser";
import { contentToNodes } from "@/components/unified/templateToDocument";
import { documentToContentNodes } from "@/components/unified/documentToContentNodes";
import { serializeTemplate } from "@/parser/templateSerializer";
import type { TemplateMeta } from "@/parser/templateSerializer";

interface SyncRefs {
  isSyncingFromMd: React.RefObject<boolean>;
  syncTimer: React.RefObject<ReturnType<typeof setTimeout> | undefined>;
  lastDoc: React.RefObject<JSONContent | null>;
  meta: React.RefObject<TemplateMeta>;
  editor: React.RefObject<Editor | null>;
  onSourceChange: React.RefObject<(source: string) => void>;
  onMetaChange: React.RefObject<((meta: Partial<TemplateMeta>) => void) | undefined>;
  onParseErrors: React.RefObject<((errors: ParseError[]) => void) | undefined>;
}

function createSyncDocToMd(refs: SyncRefs, setMdSourceState: (s: string) => void) {
  return (doc: JSONContent) => {
    if (refs.isSyncingFromMd.current) {
      return;
    }
    refs.lastDoc.current = doc;
    clearTimeout(refs.syncTimer.current);
    refs.syncTimer.current = setTimeout(() => {
      const d = refs.lastDoc.current;
      if (!d) {
        return;
      }
      const nodes = documentToContentNodes(d);
      const source = serializeTemplate(nodes, refs.meta.current);
      setMdSourceState(source);
      refs.onSourceChange.current(source);
    }, 80);
  };
}

function createSetMdSource(refs: SyncRefs, setMdSourceState: (s: string) => void) {
  return (src: string) => {
    setMdSourceState(src);
    const editor = refs.editor.current;
    if (!editor) {
      return;
    }
    refs.isSyncingFromMd.current = true;
    try {
      const result = parseTemplate(src);
      const tiptapNodes = contentToNodes(result.content.content);
      editor.commands.setContent({ content: tiptapNodes, type: "doc" });
      refs.onMetaChange.current?.({
        category: result.content.category,
        isSubTemplate: result.content.isSubTemplate,
        name: result.content.name,
        variables: result.content.variables,
      });
      refs.onParseErrors.current?.(result.errors);
    } finally {
      requestAnimationFrame(() => {
        refs.isSyncingFromMd.current = false;
      });
    }
    refs.onSourceChange.current(src);
  };
}

export function useMdTiptapSync(opts: {
  editor: Editor | null;
  meta: TemplateMeta;
  onSourceChange: (source: string) => void;
  onMetaChange?: (meta: Partial<TemplateMeta>) => void;
  onParseErrors?: (errors: ParseError[]) => void;
  initialSource?: string;
}): {
  mdSource: string;
  setMdSource: (src: string) => void;
  syncDocToMd: (doc: JSONContent) => void;
} {
  const [mdSource, setMdSourceState] = useState(opts.initialSource ?? "");

  const refs: SyncRefs = {
    editor: useRef(opts.editor),
    isSyncingFromMd: useRef(false),
    lastDoc: useRef<JSONContent | null>(null),
    meta: useRef(opts.meta),
    onMetaChange: useRef(opts.onMetaChange),
    onParseErrors: useRef(opts.onParseErrors),
    onSourceChange: useRef(opts.onSourceChange),
    syncTimer: useRef<ReturnType<typeof setTimeout>>(undefined),
  };
  refs.meta.current = opts.meta;
  refs.editor.current = opts.editor;
  refs.onSourceChange.current = opts.onSourceChange;
  refs.onMetaChange.current = opts.onMetaChange;
  refs.onParseErrors.current = opts.onParseErrors;

  const syncDocToMd = createSyncDocToMd(refs, setMdSourceState);
  const setMdSource = createSetMdSource(refs, setMdSourceState);

  return { mdSource, setMdSource, syncDocToMd };
}
